function roundMoney(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return 0;
    }
    return Math.round(numeric * 100) / 100;
}

export function buildDefaultServiceFeeRow(requiredChargeType) {
    return {
        chargeType: requiredChargeType,
        quantity: 1,
        unitRate: 0,
        totalRate: 0,
    };
}

export function getRequiredServiceFeeRow(additionalFees, requiredChargeType) {
    if (!requiredChargeType) {
        return null;
    }
    return (additionalFees ?? []).find((fee) => fee.chargeType === requiredChargeType) ?? null;
}

export function parseFeeQuantity(value) {
    const normalized = String(value ?? '').trim();
    if (normalized === '') {
        return null;
    }
    // Accept 2 / 2.0 from number inputs; reject true fractions like 2.5 (no parseInt truncation).
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
        return null;
    }
    return parsed;
}

export function parseFeeUnitRate(value) {
    const normalized = String(value ?? '').trim();
    if (normalized === '') {
        return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Recomputes totalRate only; leaves quantity/unitRate as the user typed them (including empty).
 */
export function withRecalculatedFeeTotal(fee) {
    const quantity = parseFeeQuantity(fee.quantity);
    const unitRate = parseFeeUnitRate(fee.unitRate);

    let totalRate = 0;
    if (quantity !== null && quantity > 0 && unitRate !== null && unitRate >= 0) {
        totalRate = roundMoney(quantity * unitRate);
    }

    return {
        ...fee,
        totalRate,
    };
}

export function updateAdditionalFeeRow(additionalFees, chargeType, patch) {
    return (additionalFees ?? []).map((fee) => {
        if (fee.chargeType !== chargeType) {
            return fee;
        }
        return withRecalculatedFeeTotal({ ...fee, ...patch });
    });
}

export function applyInboundCrossDockServiceFeeRate(draft, serviceFeeRate, requiredChargeType) {
    if (!requiredChargeType) {
        return draft;
    }

    const numericRate = Number(serviceFeeRate);
    const unitRate = Number.isFinite(numericRate) && numericRate >= 0 ? numericRate : 0;

    const additionalFees = (draft.additionalFees ?? []).map((fee) => {
        if (fee.chargeType !== requiredChargeType) {
            return fee;
        }
        return withRecalculatedFeeTotal({ ...fee, unitRate });
    });

    return {
        ...draft,
        additionalFees,
    };
}

export function addOptionalAdditionalFeeRow(additionalFees, chargeType) {
    const rows = additionalFees ?? [];
    if (rows.some((fee) => fee.chargeType === chargeType)) {
        return rows;
    }

    return [
        ...rows,
        withRecalculatedFeeTotal({
            chargeType,
            quantity: 1,
            unitRate: '',
            totalRate: 0,
        }),
    ];
}

export function getActiveAdditionalFees(additionalFees, removedChargeTypes) {
    const rows = additionalFees ?? [];
    if (!removedChargeTypes || removedChargeTypes.size === 0) {
        return rows;
    }
    return rows.filter((fee) => !removedChargeTypes.has(fee.chargeType));
}

export function removeAdditionalFeeRow(additionalFees, chargeType, requiredChargeType) {
    if (requiredChargeType && chargeType === requiredChargeType) {
        return additionalFees ?? [];
    }
    return (additionalFees ?? []).filter((fee) => fee.chargeType !== chargeType);
}

export function sumAdditionalFeeTotals(additionalFees) {
    return roundMoney(
        (additionalFees ?? []).reduce((sum, fee) => {
            const total = Number(fee.totalRate);
            return Number.isFinite(total) ? sum + total : sum;
        }, 0),
    );
}

export function mapAdditionalFeesForApi(additionalFees) {
    return (additionalFees ?? []).map((fee) => {
        const quantity = parseFeeQuantity(fee.quantity);
        const parsedUnitRate = parseFeeUnitRate(fee.unitRate);
        const unitRate = parsedUnitRate === null ? null : roundMoney(parsedUnitRate);
        return withRecalculatedFeeTotal({
            ...fee,
            quantity,
            unitRate,
        });
    });
}

export function getPalletCountFromAdditionalFees(additionalFees, requiredChargeType) {
    const serviceFee = getRequiredServiceFeeRow(additionalFees, requiredChargeType);
    if (!serviceFee) {
        return null;
    }
    const quantity = parseFeeQuantity(serviceFee.quantity);
    return quantity !== null && quantity > 0 ? quantity : null;
}
